import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import * as api from 'core/api';
import * as mutationTypes from 'src/store/mutation-types';
import * as i18next from 'i18next';
import { G2S } from 'core/g2s';
import router from 'src/router';
import moment from 'moment';
import * as flights from 'core/flights';
import * as filters from 'core/flights/filters';
import * as utils from 'core/utils';
import { copyFile } from 'fs';
// eslint-disable-next-line @typescript-eslint/no-var-requires
const WithRender = require('./template.html');

interface FlightData {
    class: string;
    departure: string;
    from: string;
    passengers: string;
    return: string | null;
    to: string;
}

export interface FlightsSearchResultsComponentModel {
    state: string;
    error?: string;
}

@WithRender
@Component
export default class FlightsSearchResultsComponent extends Vue {
    @Prop()
    searchFrom: string;

    @Prop()
    searchFrom2: string;

    @Prop()
    searchTo: string;

    @Prop()
    searchTo2: string;

    @Prop()
    searchDate: string;

    @Prop()
    searchDate2: string;

    @Prop()
    searchPassengers: string;

    @Prop()
    searchClass: string;

    @Prop()
    searchFilters: string;

    filters: flights.FlightFilterService | null = null;
    filteringInProgress: boolean = false;

    filterAirportsValue: {
        legs: {
            departure: number[];
            arrival: number[];
        }[];
    } | null = null;
    filterAirlines: string[] = [];
    filterStops: string = '';
    filterStopDuration: {
        range: { min: number; max: number };
        qHack: { min: number; max: number }; // Quasar QRange not firing "change" event if value changed on "input"
    } = {
        range: { min: 0, max: 0 },
        qHack: { min: 0, max: 0 } // Quasar QRange not firing "change" event if value changed on "input"
    };
    filterFlightDuration: {
        range: { min: number; max: number };
        qHack: { min: number; max: number }; // Quasar QRange not firing "change" event if value changed on "input"
    }[] = [];
    filterDepartureArrival: {
        departure: {
            range: { min: number; max: number };
            qHack: { min: number; max: number }; // Quasar QRange not firing "change" event if value changed on "input"
        };
        arrival: {
            range: { min: number; max: number };
            qHack: { min: number; max: number }; // Quasar QRange not firing "change" event if value changed on "input"
        };
    }[] = [];
    filterPrice: {
        range: { min: number; max: number };
        qHack: { min: number; max: number }; // Quasar QRange not firing "change" event if value changed on "input"
    } = {
        range: { min: 0, max: 0 },
        qHack: { min: 0, max: 0 } // Quasar QRange not firing "change" event if value changed on "input"
    };

    gaParams: utils.GoogleAnalyticsParams = new utils.GoogleAnalyticsParams();

    trackParams: utils.TrackParams = new utils.TrackParams();

    model: FlightsSearchResultsComponentModel = {
        state: 'loading'
    }

    flightSearch = new flights.FlightSearchService();
    flightData: FlightData | null = null;

    promoDetails = {
        title: '',
        description: '',
        imgUrl: ''
    }

    resultsPageSize: number = 20;
    resultsVisible: number = this.resultsPageSize;

    searchParams: flights.FlightSearchParams | null = {
        from: '',
        to: '',
        departure: null,
        return: null,
        roundtrip: false,
        class: flights.BookingClass.Economic,
        passengers: {
            adults: 0,
            children: 0,
            infants: 0
        }
    };

    flightsToFilter: flights.FlightFilterModel[] = [];
    flightsToView?: { [uniqueId: string]: flights.FlightModel } = undefined; // Force non-reactive
    flightsVisibleCount: number = 0;
    flightsVisibleParentCount: number = 0;

    searchId: string | null = null;

    selectedFlightUniqueId: string = '';
    selectedParentFlightUniqueId: string = '';

    lastHistoryStateView: string = ''; 

    gaAlreadySubmitted: string[] = [];
    
    fakingLoading: boolean = false;
    
    cheapestFlight: flights.FlightModel | {} = {};
    fasterFlight: flights.FlightModel | {} = {};
    bestFlight: flights.FlightModel | {} = {};
    urlQuery: any | null;

    subscriptionModal = {
        email: '',
        onlyFreeBaggage: false,
        onlyDirectFlight: false,
        view: 'form'
    }

    showSubscriptionButton = false;

    get departure() {
        return this.flightsToView![Object.keys(this.flightsToView!)[0]].legs[0].departureDestination;
    }

    get arrival() {
        return this.flightsToView![Object.keys(this.flightsToView!)[0]].legs[0].arrivalDestination;
    }

    get filteredSearchResults(): flights.FlightFilterModel[] {
        if (this.flightsToFilter && this.filters) {
            this.filteringInProgress = true;

            const r = this.filters.filter(this.flightsToFilter, this.flightsToView!, this.filters, this.searchParams!.roundtrip && this.flightsToFilter.length > 5);

            this.flightsVisibleCount = r.flightsVisibleCount;
            this.flightsVisibleParentCount = r.flightsVisibleParentCount;

            return this.flightsToFilter.filter(f => {
                return f.visible && !f.parentId;
            });
        } else {
            return [];
        }
    }

    get pagedSearchResults(): flights.FlightFilterModel[] {
        this.$nextTick(() => {
            this.filteringInProgress = false;
        });

        return this.filteredSearchResults.slice(0, this.resultsVisible);
    }

    get selectedFlightGroupFlights(): flights.FlightFilterModel[] {
        return this.flightsToFilter.filter((f) => {
            return f.uniqueId === this.selectedParentFlightUniqueId || f.parentId === this.selectedParentFlightUniqueId;
        });
    }

    get filterAirlineOptions() {
        const result: {
            label: string;
            value: string;
        }[] = [];

        this.filters!.airlines.items.forEach(item => {
            result.push({
                label: item.airline.name,
                value: item.airline.uniqueId
            });
        });

        return result;
    }

    get filterSortByOptions() {
        return [
            {
                label: i18next.t('label_sort_by_price'),
                value: 'price'
            },
            {
                label: i18next.t('label_sort_by_flight_duration'),
                value: 'duration'
            },
            {
                label: i18next.t('label_sort_by_departure_date'),
                value: 'departureDate'
            },
            {
                label: i18next.t('label_sort_by_arrival_date'),
                value: 'arrivalDate'
            }
        ];
    }

    get filterStopsOptions() {
        return [
            {
                label: i18next.t('label_flight_stops_filter_all'),
                value: ''
            },
            {
                label: i18next.t('label_flight_stops_filter_direct'),
                value: 'direct'
            },
            {
                label: i18next.t('label_flight_stops_filter_1stop'),
                value: '1stop'
            },
            {
                label: i18next.t('label_flight_stops_filter_2stops'),
                value: '2stops'
            }
        ];
    }

    get selectedFlight(): flights.FlightModel {
        return this.flightsToView![this.selectedFlightUniqueId];
    }

    get selectedParentFlight(): flights.FlightModel {
        return this.flightsToView![this.selectedParentFlightUniqueId];
    }

    get totalPassengers() {
        return this.searchParams!.passengers.adults
            + this.searchParams!.passengers.children
            + this.searchParams!.passengers.infants;
    }

    get className() {
        return flights.BookingClass.classLabel(this.searchParams!.class);
    }

    get isWebview(): boolean {
        return this.urlQuery?.utm_source == 'mobile' && this.urlQuery?.utm_medium == 'app';
    }

    mounted() {
        this.urlQuery = utils.Http.getUrlParameters();
        window.document.title = i18next.t('title_flight_tickets');

        window.history.replaceState({ ...window.history.state, view: 'list' }, '', router.currentRoute.fullPath);
        this.setLastHistoryStateView();
        window.addEventListener('popstate', this.onPopState);

        this.loadModel();
        window.addEventListener('scroll', this.showMoreResults);

        const mo = utils.Cookies.getMarkerOwner();

        if (G2S.config.theme !== 'aviasales' && ['219038', ''].includes(mo)) {
            this.showSubscriptionButton = true;
        }
    }

    showMoreResults(){
        const element = document.getElementById('loading-trigger');
        if(this.resultsVisible < this.flightsVisibleParentCount){
            if (element && (<any>element).getBoundingClientRect().bottom < window.innerHeight) {
                this.fakingLoading = true;
                this.resultsVisible += this.resultsPageSize;
                this.fakingLoading = false;
            }
        }
    }

    beforeDestroy() {
        window.removeEventListener('popstate', this.onPopState);
    }

    setLastHistoryStateView() {
        this.lastHistoryStateView = window.history.state && window.history.state.view ? window.history.state.view : '';
    }

    onPopState(event) {
        if (event.state && event.state.view == 'flight' && this.lastHistoryStateView == 'list') {
            if (this.selectedParentFlight) {
                this.showFlightGroup(this.selectedParentFlight);
            }
            this.showFlightDetails(this.selectedFlight);
        }
        else if (event.state && event.state.view == 'list' && this.lastHistoryStateView == 'flight') {
            this.hideFlightDetails();
            this.hideFlightGroup();
        }
        else if (event.state && event.state.view == 'flightGroup' && this.lastHistoryStateView == 'list') {
            this.showFlightGroup(this.selectedFlight);
        }
        else if (event.state && event.state.view == 'flight' && this.lastHistoryStateView == 'flightGroup') {
            this.showFlightDetails(this.selectedFlight);
        }
        else if (event.state && event.state.view == 'flightGroup' && this.lastHistoryStateView == 'flight') {
            this.hideFlightDetails();
        }
        else if (event.state && event.state.view == 'list' && this.lastHistoryStateView == 'flightGroup') {
            this.hideFlightGroup();
        }
    }

    
    @Watch('filteredSearchResults')
    setFirstFlights() {
        if(Object.keys(this.cheapestFlight).length === 0 &&
           Object.keys(this.bestFlight).length === 0 &&
           Object.keys(this.fasterFlight).length === 0) {
            this.cheapestFlight = this.getCheapestFlight(this.filteredSearchResults);
            this.fasterFlight = this.getFasterFlight(this.filteredSearchResults);
            this.bestFlight = this.getBestFlight(this.filteredSearchResults);
        }
    }
    
    @Watch('filterStops')
    onFilterStopsChanged() {
        this.filters!.stopCount.clear();

        switch (this.filterStops) {
            case 'direct':
                this.filters!.stopCount.none = true;
                break;
            case '1stop':
                this.filters!.stopCount.one = true;
                break;
            case '2stops':
                this.filters!.stopCount.two = true;
                break;
        }
    }

    onFilterStopDurationInpit(value: { min: number; max: number }) {
        this.filterStopDuration.qHack = value; // Quasar QRange not firing "change" event if value changed on "input"
    }

    onFilterStopDurationChange(value: { min: number; max: number }) {
        this.filterStopDuration.range = value;
        this.filters!.stopDuration.rangeMin = value.min;
        this.filters!.stopDuration.rangeMax = value.max;

        (<any>this).$ga.sendEvent('flights.results', 'filters.apply.duration.transfer', 'flights.results.filters.apply.duration.transfer', this.gaParams);
    }

    onFilterFlightDurationInput(value: { min: number; max: number }, index: number) {
        this.filterFlightDuration[index].qHack = value; // Quasar QRange not firing "change" event if value changed on "input"
    }

    onFilterFlightDurationChange(value: { min: number; max: number }, index: number) {
        this.filterFlightDuration[index].range = value;
        this.filters!.flightDuration.items[index].rangeMin = value.min;
        this.filters!.flightDuration.items[index].rangeMax = value.max;

        (<any>this).$ga.sendEvent('flights.results', 'filters.apply.time.flight', 'flights.results.filters.apply.time.flight', this.gaParams);
    }

    onFilterDepartureArrivalInput(value: { min: number; max: number }, index: number, type: 'arrival' | 'departure') {
        this.filterDepartureArrival[index][type].qHack = value; // Quasar QRange not firing "change" event if value changed on "input"
    }

    onFilterDepartureArrivalChange(value: { min: number; max: number }, index: number, type: 'arrival' | 'departure') {
        this.filterDepartureArrival[index][type].range = value;
        this.filters!.departureArrival.items[index][type].rangeMin = value.min;
        this.filters!.departureArrival.items[index][type].rangeMax = value.max;

        (<any>this).$ga.sendEvent('flights.results', 'filters.apply.time.departure.arrival', 'flights.results.filters.apply.time.departure.arrival', this.gaParams);
    }

    onFilterAirportsInput(value: number[], legIndex, destinationKind: string) {
        this.filters!.airports.legs[legIndex][destinationKind].items.forEach((item: filters.AirportsFilterItem) => {
            item.checked = value.indexOf(item.destination.uniqueId) >= 0;
        });

        (<any>this).$ga.sendEvent('flights.results', 'filters.apply.airport', 'flights.results.filters.apply.airport', this.gaParams);
    }

    onFilterAirlinesInput(value: string) {
        if(value.length) {
            (<any>this).$ga.sendEvent('flights.results', 'filters.apply.airline', 'flights.results.filters.apply.airline', this.gaParams);
        }
    }

    @Watch('filterAirlines')
    onFilterAirlinesChanged() {
        this.filters!.airlines.clear();

        this.filters!.airlines.items.forEach(item => {
            item.checked = this.filterAirlines.indexOf(item.airline.uniqueId) >= 0;
        });
    }

    onFilterPriceInput(value: { min: number; max: number }) {
        this.filterPrice.qHack = value; // Quasar QRange not firing "change" event if value changed on "input"
    }

    onFilterPriceChange(value: { min: number; max: number }) {
        this.filterPrice.range = value;
        this.filters!.price.rangeMin = value.min;
        this.filters!.price.rangeMax = value.max;

        (<any>this).$ga.sendEvent('flights.results', 'filters.apply.price', 'flights.results.filters.apply.price', this.gaParams);
    }

    @Watch('$route')
    onRouteChanged() {
        this.loadModel();
    }

    flightDurationLabel(index: number, type: 'min' | 'max'): string {
        if (this.filterFlightDuration && this.filterFlightDuration.length > 0) {
            return this.durationFormat(this.filterFlightDuration[index].qHack[type]);
        } else {
            return '';
        }
    }

    departureArrivalTimeLabel(unixTime) {
        return moment.unix(unixTime).utc().format(i18next.t('format_time'));
    }

    departureArrivalDayLabel(unixTime) {
        return moment.unix(unixTime).utc().format(i18next.t('format_date_md_short'));
    }

    getMoreLabel(): string {
        const b = i18next.t('numerous_flights', { count: this.resultsPageSize });
        return i18next.t('label_show_more_withvalue', <any>{ i: b });
    }

    getSegmentLabel(leg: flights.LegModel, segment: flights.SegmentModel, segmentIndex: number): string {
        let result = '';

        if (segment.arrivalDestination.iataCode === leg.segments[segmentIndex + 1].departureDestination.iataCode) {
            result += segment.arrivalDestination.iataCode;
        } else {
            result += segment.arrivalDestination.city.iataCode;
        }

        if (leg.segments.length > 1 && segmentIndex < (leg.segments.length - 2)) {
            result += ', ';
        }

        return result;
    }

    getFilterAirportsOptions(legIndex: number, destinationKind: string) {
        const filterItems = this.filters!.airports.legs[legIndex][destinationKind].items;

        if (filterItems && filterItems.length) {
            return filterItems.map((item: filters.AirportsFilterItem) => {
                return {
                    label: item.destination.localizedName,
                    value: item.destination.uniqueId
                };
            });
        } else {
            return [];
        }
    }

    loadModel() {
        if (this.searchFrom2 && this.searchTo2 && this.searchDate2) {
            this.searchParams = new flights.FlightNavigationService().getRoundtripParams(
                this.searchFrom, this.searchTo, this.searchDate, this.searchFrom2, this.searchTo2, this.searchDate2, this.searchPassengers, this.searchClass
            )!;
        }
        else {
            this.searchParams = new flights.FlightNavigationService().getOnewayParams(this.searchFrom, this.searchTo, this.searchDate, this.searchPassengers, this.searchClass);
        }

        // Fighting Yandex bot
        if (!this.searchParams) {
            router.push('/');
            return;
        }

        // Init GA params
        this.gaParams = new utils.GoogleAnalyticsParams();
        (<any>this).$ga.addSearchParams(this.gaParams, this.searchParams);

        (<any>this).$ga.sendPageview('/flights/search/start', 'Flights / Search / Start', this.gaParams);

        // Tracking
        utils.TrackingService.updateParamsFromSearch(this.trackParams, this.searchParams);
        utils.TrackingService.track(
            utils.TrackEvent.FlightSearchStart,
            this.trackParams
        );

        document.getElementById('pixels')!.innerHTML = '';
        utils.PixelsService.setPixelForFlightSearch(document.getElementById('pixels')!, this.searchParams);

        this.model.state = 'loading';

        // Promo modal
        this.showPromoModal();

        // Save search params to state to display them on search form
        this.$store.commit(mutationTypes.SEARCH_FLIGHTS, this.searchParams);

        this.flightSearch.addHistoryItem(this.searchParams);

        this.flightSearch.search(this.searchParams).then(
            (result: flights.FlightSearchResult) => {
                new api.RetailRocket().searchCompleted({
                    searchId: result.searchId,
                    searchPhrase: router.currentRoute.fullPath.replace('/flights/', ''),
                    mailTrackingId: <any>this.$route.query.MailTrackingId
                });

                this.gaParams.metric1 = result.duration;

                if (result && result.flights && result.flights.length > 0) {
                    this.searchId = result.searchId;
                    this.flightsToFilter = result.flightsToFilter;
                    this.filters = new flights.FlightFilterService(result.flights, this.searchFilters);
                    this.filterStopDuration = {
                        qHack: { // Quasar QRange not firing "change" event if value changed on "input"
                            min: this.filters.stopDuration.rangeMin,
                            max: this.filters.stopDuration.rangeMax
                        },
                        range: {
                            min: this.filters.stopDuration.rangeMin,
                            max: this.filters.stopDuration.rangeMax
                        }
                    };
                    this.filters.flightDuration.items.forEach((item) => {
                        this.filterFlightDuration.push({
                            qHack: { // Quasar QRange not firing "change" event if value changed on "input"
                                min: item.rangeMin,
                                max: item.rangeMax
                            },
                            range: {
                                min: item.rangeMin,
                                max: item.rangeMax
                            }
                        });
                    });
                    this.filters.departureArrival.items.forEach((item) => {
                        this.filterDepartureArrival.push({
                            departure: {
                                qHack: { // Quasar QRange not firing "change" event if value changed on "input"
                                    min: item.departure.rangeMin,
                                    max: item.departure.rangeMax
                                },
                                range: {
                                    min: item.departure.rangeMin,
                                    max: item.departure.rangeMax
                                }
                            },
                            arrival: {
                                qHack: { // Quasar QRange not firing "change" event if value changed on "input"
                                    min: item.arrival.rangeMin,
                                    max: item.arrival.rangeMax
                                },
                                range: {
                                    min: item.arrival.rangeMin,
                                    max: item.arrival.rangeMax
                                }
                            }
                        });
                    });
                    this.filterAirportsValue = {
                        legs: []
                    };
                    this.filters.airports.legs.forEach(() => {
                        this.filterAirportsValue!.legs.push({
                            departure: [],
                            arrival: []
                        });
                    });
                    this.filterPrice = {
                        qHack: { // Quasar QRange not firing "change" event if value changed on "input"
                            min: this.filters.price.rangeMin,
                            max: this.filters.price.rangeMax
                        },
                        range: {
                            min: this.filters.price.rangeMin,
                            max: this.filters.price.rangeMax
                        }
                    };
                    this.flightsToView = {};

                    result.flights.forEach(f => {
                        this.flightsToView![f.uniqueId] = f;
                    });

                    this.flightsVisibleCount = this.flightsToFilter.length;
                    this.flightsVisibleParentCount = this.flightsVisibleCount;

                    if (this.filters.airlines.touched) {
                        this.filters.airlines.items.forEach(item => {
                            if (item.checked) {
                                this.filterAirlines.push(item.airline.uniqueId);
                            }
                        });
                    }

                    this.model.state = 'results';

                    this.gaParams.metric2 = result.flights.length;

                    (<any>this).$ga.sendPageview('/flights/search/results', 'Flights / Search / Results', this.gaParams);

                    utils.TrackingService.track(
                        utils.TrackEvent.FlightSearchResults,
                        this.trackParams,
                        {
                            FlightCount: result.flights.length
                        }
                    );
                } else {
                    this.model.state = 'empty';

                    // (<any>this).$ga.sendPageview('/flights/search/results', 'Flights / Search / Results', this.gaParams);

                    (<any>this).$ga.sendEvent('flights.results', 'msg.noresults', 'flights.results.msg.noresults', this.gaParams);

                    utils.TrackingService.track(
                        utils.TrackEvent.FlightSearchEmpty,
                        this.trackParams
                    );
                }
            },
            (error) => {
                this.model.error = i18next.t('error_server_tryagain');

                if (error.response) {
                    if (error.response.status === 400) {
                        this.model.error = i18next.t('error_search_wrong_params');
                    }
                    console.log('XHR failed', error.response.status, error.response.data);
                } else {
                    console.log('XHR error', error.message);
                }

                this.model.state = 'error';

                (<any>this).$ga.sendEvent('flights.results', 'msg.error', 'flights.results.msg.error', this.gaParams);

                utils.TrackingService.track(
                    utils.TrackEvent.FlightSearchError,
                    this.trackParams
                );
            }
        );
    }

    onStopOptionsChange(value: any) {
        switch (value) {
            case 'direct':
                (<any>this).$ga.sendEvent('flights.results', 'filters.apply.direct.only', 'flights.results.filters.apply.direct.only', this.gaParams);
                break;
            case '1stop':
                (<any>this).$ga.sendEvent('flights.results', 'filters.apply.one.transfer', 'flights.results.filters.apply.one.transfer', this.gaParams);
                break;   
            case '2stops':
                (<any>this).$ga.sendEvent('flights.results', 'filters.apply.many.transfer', 'flights.results.filters.apply.many.transfer', this.gaParams);
                break;
        }
    }

    onFlightSubmit() {
        if (this.selectedFlight?.externalPartner
            && this.searchId
            && this.selectedFlight?.uniqueId) {
            const routeData = router.resolve({
                path: '/partner',
                query: {
                    sid: this.searchId,
                    fid: this.selectedFlight.uniqueId,
                    pn: this.selectedFlight.externalPartner.name
                }
            });

            const partnerName: string = this.selectedFlight.externalPartner.name.replace(/\s/g, '').toLowerCase();
            (<any>this).$ga.sendEvent('flights.results', `btn.partner.${partnerName}`, `flights.results.btn.partner.${partnerName}`, this.gaParams);

            window.open(routeData.href, '_blank');
        } else {
            if (this.selectedFlight.promo_coupon_name) {
                utils.Cookies.g2scpnSet(this.selectedFlight.promo_coupon_name);
            }
            router.push(`${new flights.FlightNavigationService().getBookingUrl(this.searchParams!, this.searchId!, this.selectedFlightUniqueId)}`);
        }
        (<any>this).$ga.sendEvent('flights.results', 'flight.buy', 'flights.results.flight.buy', this.gaParams);
    }

    onFlightDismiss() {
        window.history.go(-1);
    }

    onFlightBack() {
        window.history.go(-1);
        if((<any>this.$refs).modalFlightGroup.showing){
            (<any>this).$ga.sendPageview('/flights/search/results/group/details', 'Flights / Search / Results / Group / Details', this.gaParams);
        }
    }

    onFlightGroupDismiss() {
        window.history.go(-1);
    }

    onFlightGroupBack() {
        window.history.go(-1);
    }

    onFiltersApply() {
        (<any>this.$refs).modalFilters.hide();
        if(!this.gaAlreadySubmitted.includes('flights.results.filters.apply')) {
            (<any>this).$ga.sendEvent('flights.results', 'filters.apply', 'flights.results.filters.apply', this.gaParams);
            this.gaAlreadySubmitted.push('flights.results.filters.apply');
        }
    }

    onFiltersReset() {
        this.filterStopDuration = {
            qHack: { // Quasar QRange not firing "change" event if value changed on "input"
                min: this.filters!.stopDuration.min,
                max: this.filters!.stopDuration.max
            },
            range: {
                min: this.filters!.stopDuration.min,
                max: this.filters!.stopDuration.max
            }
        };
        this.filterFlightDuration.forEach((item, index) => {
            item.qHack = { // Quasar QRange not firing "change" event if value changed on "input"
                min: this.filters!.flightDuration.items[index].min,
                max: this.filters!.flightDuration.items[index].max
            };
            item.range = {
                min: this.filters!.flightDuration.items[index].min,
                max: this.filters!.flightDuration.items[index].max
            };
        });
        this.filterDepartureArrival.forEach((item, index) => {
            item.departure = {
                qHack: { // Quasar QRange not firing "change" event if value changed on "input"
                    min: this.filters!.departureArrival.items[index].departure.min,
                    max: this.filters!.departureArrival.items[index].departure.max
                },
                range: {
                    min: this.filters!.departureArrival.items[index].departure.min,
                    max: this.filters!.departureArrival.items[index].departure.max
                }
            };
            item.arrival = {
                qHack: { // Quasar QRange not firing "change" event if value changed on "input"
                    min: this.filters!.departureArrival.items[index].arrival.min,
                    max: this.filters!.departureArrival.items[index].arrival.max
                },
                range: {
                    min: this.filters!.departureArrival.items[index].arrival.min,
                    max: this.filters!.departureArrival.items[index].arrival.max
                }
            };
        });
        this.filterAirportsValue!.legs.forEach((leg) => {
            leg.departure = [];
            leg.arrival = [];
        });
        this.filterPrice = {
            qHack: { // Quasar QRange not firing "change" event if value changed on "input"
                min: this.filters!.price.min,
                max: this.filters!.price.max
            },
            range: {
                min: this.filters!.price.min,
                max: this.filters!.price.max
            }
        };

        this.filters!.clear();

        (<any>this.$refs).modalFilters.hide();

        (<any>this).$ga.sendEvent('flights.results', 'filters.reset', 'flights.results.filters.reset', this.gaParams);
        this.gaAlreadySubmitted = this.gaAlreadySubmitted.filter((ga) => ga !== 'flights.results.filters.apply');
    }

    onFlightTerms() {
        if (this.selectedFlight) {
            (<any>this.$refs).flightTerms.show(this.searchId, this.selectedFlight.uniqueId);

            (<any>this).$ga.sendEvent('flights.results', 'flight.terms', 'flights.results.flight.terms', this.gaParams);
        }
    }

    onModalFilters() {
        (<any>this.$refs).modalFilters.show();

        (<any>this).$ga.sendEvent('flights.results', 'filters.open', 'flights.results.filters.open', this.gaParams);
    }

    showFlightDetails(flight) {
        this.setLastHistoryStateView();
        this.selectedFlightUniqueId = flight.uniqueId;
        (<any>this.$refs).flightDetails?.show();
        // Track add to cart
        (<any>this).$ga.addFlightProduct(this.selectedFlight, this.totalPassengers);
        (<any>this).$ga.setAction('add', null);
        
        (<any>this).$ga.sendEvent('flights.results', 'flight.details', 'flights.results.flight.details', this.gaParams);
        (<any>this).$ga.sendPageview('/flights/search/results/details', 'Flights / Search / Results / Details', this.gaParams);
    }

    hideFlightDetails() {
        this.setLastHistoryStateView();
        (<any>this.$refs).flightDetails.hide();
    }

    showFlightGroup(flight) {
        this.setLastHistoryStateView();
        this.selectedParentFlightUniqueId = flight.uniqueId;
        (<any>this.$refs).modalFlightGroup.show();
        (<any>this).$ga.sendEvent('flights.results', 'flight.group.details', 'flights.results.flight.group.details', this.gaParams);
        (<any>this).$ga.sendPageview('/flights/search/results/group/details', 'Flights / Search / Results / Group / Details', this.gaParams);
    }

    hideFlightGroup() {
        this.setLastHistoryStateView();
        (<any>this.$refs).modalFlightGroup.hide();
    }

    onSelectFlight(flight) {
        // if (flight.childCount > 0) {
        //     window.history.pushState({ ...window.history.state, view: 'flightGroup' }, '', router.currentRoute.fullPath);
        //     this.showFlightGroup(flight);
        // } else {
        window.history.pushState({ ...window.history.state, view: 'flight' }, '', router.currentRoute.fullPath);
        if(this.isWebview){
            this.setLastHistoryStateView();
            this.selectedFlightUniqueId = flight.uniqueId;
            this.onFlightSubmit();
        } else {
            this.showFlightDetails(flight);
        }
        // }
    }

    onSelectFlightGroupFlight(flight) {
        window.history.pushState({ ...window.history.state, view: 'flight' }, '', router.currentRoute.fullPath);
        this.showFlightDetails(flight);
    }

    showPromoModal() {
        const utmCompaign = utils.Http.getUrlParameter('utm_campaign');
        if (utmCompaign == 'social-vk-2018-08'
            && this.searchParams!.to) {
            this.promoDetails.imgUrl = G2S.config.cdnUrl + '/p/d/' + this.searchParams!.to + '.600x300.jpg';

            switch(this.searchParams!.to) {
                case 'BOJ':
                    this.promoDetails.title = 'Бургас (Болгария)';
                    this.promoDetails.description =
                        '<p>В городе Бургас вы отдохнете на море, посетите старинные минеральные бани и увидите руины античных городов.</p>'
                        + '<p>Посетите Бургас вместе с нами. А самые выгодные билеты для вас мы уже нашли&nbsp;:)</p>';
                    break;
                case 'TIV':
                    this.promoDetails.title = 'Тиват (Черногория)';
                    this.promoDetails.description =
                        '<p>Тиват расположен в уютной бухте, отделяющей это место от других городов. В период средневековья здесь любили отдыхать знаменитые вельможи Европы.</p>'
                        + '<p>Посетите Тиват вместе с нами. А самые выгодные билеты для вас мы уже нашли&nbsp;:)</p>';
                    break;
                case 'DPS':
                    this.promoDetails.title = 'Денпасар (Индонезия)';
                    this.promoDetails.description =
                        '<p>Денпасар — город свободы, в котором переплетены восточная и западная культуры. Здесь вы увидите Катур Мюка — стража города, который защищает его от злых сил.</p>'
                        + '<p>Посетите Денпасар вместе с нами. А самые выгодные билеты для вас мы уже нашли&nbsp;:)</p>';
                    break;
                case 'TBS':
                    this.promoDetails.title = 'Тбилиси (Грузия)';
                    this.promoDetails.description =
                        '<p>Тбилиси впитал дыхание Европы, древнюю кавказскую историю и наследие Советского Союза. Первые упоминания об этом городе появились в V веке н.э.</p>'
                        + '<p>Посетите Тбилиси вместе с нами. А самые выгодные билеты для вас мы уже нашли&nbsp;:)</p>';
                    break;
                case 'BCN':
                    this.promoDetails.title = 'Барселона (Испания)';
                    this.promoDetails.description =
                        '<p>В Барселоне жили и работали Пабло Пикассо и Сальвадор Дали. Пикассо настоял на том, чтобы его музей был построен именно здесь. А лучший вид на Барселону открывается с холма Тибидабо.</p>'
                        + '<p>Посетите Барселону вместе с нами. А самые выгодные билеты для вас мы уже нашли&nbsp;:)</p>';
                    break;
                default:
                    this.promoDetails.title = this.searchParams!.to;
                    break;
            }

            (<any>this.$refs).modalPromo.show();
        }
    }

    durationFormat(mins: number): string {
        return (<any>moment.duration(mins, 'minutes')).format(i18next.t('format_duration'));
    }

    legsHasDifferentAirlines(flight: flights.FlightModel) {
        return flight.legs[0].segments[0].airline.iataCode !== flight.legs[1].segments[0].airline.iataCode;
    }

    getLegsSegmentsAirlinesDistinct(flight: flights.FlightModel) {
        const result: api.Airline[] = [];
        const map: string[] = [];
        const segments: flights.SegmentModel[] = [];

        flight.legs.forEach((leg) => {
            leg.segments.forEach((segment) => {
                segments.push(segment);
            });
        });

        for (const segment of segments) {
            if (!map.includes(segment.airline.uniqueId)) {
                map.push(segment.airline.uniqueId);
                result.push(segment.airline);
            }
        }

        return result;
    }

    sumFast(flight: flights.FlightModel) {
        return flight.legs.reduce((total, f) => total + f.durationMinutes, 0) / 60;
    }
    
    getCheapestFlight(flightsForSort: flights.FlightFilterModel[]) {
        const flightsSortFast = [...flightsForSort].sort(
            utils
                .firstBy((v1: flights.FlightFilterModel, v2: flights.FlightFilterModel) => { return (<any>this).flightsToView[v1.uniqueId].billingPrice - (<any>this).flightsToView[v2.uniqueId].billingPrice; })
                .thenBy((v1: flights.FlightFilterModel, v2: flights.FlightFilterModel) => { return this.sumFast((<any>this).flightsToView[v1.uniqueId]) - this.sumFast((<any>this).flightsToView[v2.uniqueId]); })
        );

        return (<any>this).flightsToView[flightsSortFast[0].uniqueId];
    }

    getFasterFlight(flightsForSort: flights.FlightFilterModel[]) {
        const flightsSortFast = [...flightsForSort].sort(
            utils
                .firstBy((v1: flights.FlightFilterModel, v2: flights.FlightFilterModel) => { return this.sumFast((<any>this).flightsToView[v1.uniqueId]) - this.sumFast((<any>this).flightsToView[v2.uniqueId]); })
                .thenBy((v1: flights.FlightFilterModel, v2: flights.FlightFilterModel) => { return (<any>this).flightsToView[v1.uniqueId].billingPrice - (<any>this).flightsToView[v2.uniqueId].billingPrice; })
        );

        return (<any>this).flightsToView[flightsSortFast[0].uniqueId];
    }

    // Желательно переписать и отправить в core @todo
    getBestFlight(flightFilters: flights.FlightFilterModel[]) {
        const localFlightFilters = [...flightFilters];
        const resByTime = localFlightFilters.sort((a, b) => this.sumFast((<any>this).flightsToView[a.uniqueId]) - this.sumFast((<any>this).flightsToView[b.uniqueId])).map((e, i) => ({
            uid: e.uniqueId,
            index: i,
            value: this.sumFast((<any>this).flightsToView[e.uniqueId]),
            priority: (<any>this).flightsToView[e.uniqueId]
        }));
        
        const resByCost = localFlightFilters.sort((a, b) => (<any>this).flightsToView[a.uniqueId].billingPrice - (<any>this).flightsToView[b.uniqueId].billingPrice).map((e, i) => ({
            uid: e.uniqueId,
            index: i,
            value: (<any>this).flightsToView[e.uniqueId].billingPrice,
            priority: (<any>this).flightsToView[e.uniqueId]
        }));

        const swap = (json)=>{
            const ret = {};
            for(const key in json) {
                ret[json[key].uid] = json[key].rank;
            }
            return ret;
        };                
        
        const setRank = (elems) => {  
            let prevValue = null;
            let rankIndex = 1;
            
            for (const elem of elems) {
                if (!prevValue || elem.value == (<any>prevValue).value) {
                    elem.rank = rankIndex;
                } else {
                    elem.rank = ++rankIndex;
                }
                
                prevValue = elem;
                
            }
            
            return elems;
        };
        
        const merge = (time, cost) => {
            const res = {};
            
            for (const key in time) {
                res[key] = parseFloat(cost[key]) + parseFloat(time[key]) * 0.5;
            }
                
            return res;
        };
        
        const result = merge(
            swap(setRank(resByTime)),
            swap(setRank(resByCost))
        );

        return (<any>this).flightsToView[localFlightFilters.sort((a, b) => result[a.uniqueId] - result[b.uniqueId])[0].uniqueId];
        
    }

    updateFlightData() {
        const lastHistoryItem = this.flightSearch.getHistoryItems()[0];

        if (lastHistoryItem) {
            this.flightData = {
                class: lastHistoryItem.class,
                departure: moment(lastHistoryItem.departure!).format(i18next.t('format_date_lg_wd')),
                from: lastHistoryItem.from,
                passengers: i18next.t('label_passengers_and_class_value', <any>{
                    i: lastHistoryItem.passengers,
                    j: i18next.t('label_flight_class_' + lastHistoryItem.class.toLowerCase())
                }),
                return: lastHistoryItem.return ? moment(lastHistoryItem.return!).format(i18next.t('format_date_lg_wd')) : null,
                to: lastHistoryItem.to
            };
        }
    }

    openSubscriptionModal() {
        (<any>this).$ga.sendEvent('price subscription', 'btn.click', this.getFlightDataGaLabel(), this.gaParams);
        this.updateFlightData();
        this.subscriptionModal.view = 'form';
        (<any>this.$refs).modalSubscription.show();
    }

    getFlightDataGaLabel() {
        const p = location.pathname.split('/');

        if (p.length === 7) {
            return `${p[2]}-${p[3]}.${p[4]}-0000.${p[5].split('').join('-')}`;
        } else {
            return `${p[2]}-${p[3]}.${p[4]}-${p[7]}.${p[8].split('').join('-')}`;
        }
    }

    subscribe() {
        if (this.searchId && this.subscriptionModal.email) {
            this.subscriptionModal.view = 'loading';
            new api.BestPriceService().subscribe({
                searchId: this.searchId,
                email: this.subscriptionModal.email,
                onlyFreeBaggage: this.subscriptionModal.onlyFreeBaggage,
                onlyDirectFlight: this.subscriptionModal.onlyDirectFlight
            }).then(
                () => {
                    this.subscriptionModal.view = 'success';
                },
                error => {
                    this.subscriptionModal.view = 'error';
                    console.error(error);
                }
            );
        }
    }
}
